import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import * as yup from 'yup'

import * as styles from './styles.module.scss'

import { Modal } from '../modal'
import { AdjustModalProps } from './types'
import { Col, Row } from 'react-grid-system'
import { Dropdown, Input } from '../form/fields'

import { useAdjustQuantity, useForm } from '../../../hooks'
import { Option } from '../form/fields/dropdown/types'
import { Severity } from '../../../types'
import { LoadingOverlay, Button } from '..'
import { integerMsg, positiveIntegerMsg, requiredMsg } from './constants'

const AdjustModal = ({
   isOpen,
   setIsOpen,
   ItemId,
   onAdjustComplete,
   enableLocationTracking,
   handleSnackbar,
   selectedWarehouse,
   handleWarehouseChange,
   warehouseOptions,
   itemWarehouses,
   disabledWarehouseSelection,
   isQuantityItem,
   connectionOptions,
   locationOptions,
}: AdjustModalProps) => {
   const [selectedReason, setSelectedReason] = useState<string | null>(null)

   const {
      adjustReasons,
      adjustQuantity,
      error,
      isError,
      isLoading,
      isSuccess,
      isReasonsSuccess,
      isReasonsError,
   } = useAdjustQuantity()

   const adjustSchema = useMemo(() => {
      const isReturn = selectedReason === 'Return'
      return yup.object().shape({
         ItemId: yup.number().required(),
         Reason: yup.string().required(requiredMsg),
         Quantity:
            selectedReason !== 'Inventory adjustment'
               ? yup
                    .number()
                    .typeError(positiveIntegerMsg)
                    .integer(positiveIntegerMsg)
                    .positive(positiveIntegerMsg)
                    .required(requiredMsg)
               : yup
                    .number()
                    .typeError(integerMsg)
                    .integer(integerMsg)
                    .required(requiredMsg),
         Connection: isReturn ? yup.string().required(requiredMsg) : null,
         Location: isReturn ? yup.string() : null,
      })
   }, [selectedReason])

   const { onSubmit, validationErrors } = useForm(adjustSchema, (values) => {
      if (values) {
         const toolWarehouseId =
            itemWarehouses?.find(
               (wh) => wh.WarehouseLocationId === selectedWarehouse.id
            )?.ToolWarehouseID ?? null
         const apiPayload: AdjustQuantityPayload =
            selectedReason === 'Return'
               ? {
                    ToolId: Number(values.ItemId),
                    AdjustReason: Number(values.Reason),
                    Quantity: Number(values.Quantity),
                    ConnectionId: values?.Connection,
                    LocationId: values?.Location
                       ? Number(values?.Location)
                       : undefined,
                    toolWarehouseId: toolWarehouseId,
                 }
               : {
                    ToolId: Number(values.ItemId),
                    AdjustReason: Number(values.Reason),
                    Quantity: Number(values.Quantity),
                    toolWarehouseId: toolWarehouseId,
                 }
         adjustQuantity(apiPayload)
      }
   })

   const handleClose = useCallback(() => setIsOpen(false), [])

   const handleReasonChange = useCallback(
      (option: Option) => setSelectedReason(option?.label ?? null),
      []
   )

   useEffect(() => {
      if (isReasonsError) {
         handleSnackbar(error, Severity.ERROR)
      }
   }, [isReasonsError])

   useEffect(() => {
      if (isError) {
         const msg = error ?? 'There was an error adjusting the quantity'
         handleSnackbar(msg, Severity.ERROR)
         onAdjustComplete()
      }
   }, [isError])

   useEffect(() => {
      if (isSuccess) {
         handleSnackbar('The quantity was adjusted', Severity.SUCCESS)
         onAdjustComplete()
      }
   }, [isSuccess])

   const reasonOptions = useMemo(() => {
      if (isReasonsSuccess) {
         const allReasons = adjustReasons?.map((reason) => ({
            value: reason.Value,
            label: reason.Text,
         }))
         return isQuantityItem
            ? allReasons.filter((reason) => reason.label !== 'Return') // only show return option for consumables
            : allReasons
      }
   }, [adjustReasons, isReasonsSuccess, isQuantityItem])

   const defaultWarehouse = useMemo(
      () =>
         selectedWarehouse
            ? warehouseOptions.find(
                 (wh) => Number(wh.value) === selectedWarehouse?.id
              )
            : null,
      [selectedWarehouse, warehouseOptions]
   )

   return (
      <>
         {isLoading && <LoadingOverlay />}
         <Modal
            isModalVisible={isOpen}
            closeModal={handleClose}
            title="Adjust"
            maxWidth="713px"
            zIndex="999"
            stickyHeader
         >
            <form
               id="adjust-modal"
               noValidate
               onSubmit={onSubmit}
               className={styles.modalContent}
            >
               <input type="hidden" name="ItemId" value={ItemId} />
               <Row>
                  <Col xs={12}>
                     {enableLocationTracking && (
                        <Dropdown
                           error={
                              enableLocationTracking && !selectedWarehouse
                                 ? 'This field is required.'
                                 : null
                           }
                           defaultValue={defaultWarehouse}
                           name="Warehouse"
                           options={warehouseOptions}
                           id="Warehouse"
                           label="Warehouse"
                           placeholder="Warehouse"
                           onChange={handleWarehouseChange}
                           isMulti={false}
                           required
                           disabled={disabledWarehouseSelection}
                        />
                     )}
                     <Dropdown
                        error={validationErrors?.Reason}
                        name="Reason"
                        onChange={handleReasonChange}
                        options={reasonOptions}
                        id="Reason"
                        label="Reason"
                        placeholder="Reason"
                        isMulti={false}
                        required
                     />
                     <Input
                        error={validationErrors?.Quantity}
                        id="Quantity"
                        label="Quantity"
                        inputProps={{
                           type: 'number',
                        }}
                        required
                     />
                     {selectedReason === 'Return' && (
                        <>
                           <Dropdown
                              error={validationErrors?.Connection}
                              name="Connection"
                              options={connectionOptions}
                              id="Connection"
                              label="Connection"
                              placeholder="Connection"
                              isMulti={false}
                              required
                           />
                           {enableLocationTracking && (
                              <Dropdown
                                 error={validationErrors?.Location}
                                 name="Location"
                                 options={locationOptions}
                                 id="Location"
                                 label="Location (optional)"
                                 placeholder="Location (optional)"
                                 isMulti={false}
                              />
                           )}
                        </>
                     )}
                  </Col>
               </Row>
               <Row>
                  <Col xs={12} sm={6}>
                     <Button
                        variant="primary"
                        type="submit"
                        form="adjust-modal"
                        minWidth="100%"
                        disabled={enableLocationTracking && !selectedWarehouse}
                     >
                        Save
                     </Button>
                  </Col>
                  <Col xs={12} sm={6}>
                     <Button
                        variant={'tertiary'}
                        type="button"
                        onClick={handleClose}
                        minWidth="100%"
                        isReset
                     >
                        Close
                     </Button>
                  </Col>
               </Row>
            </form>
         </Modal>
      </>
   )
}

export default memo(AdjustModal)
